import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { getDamageLogs, getDamageLogConfig, getDamageLogAnalytics, createDamageLog, updateDamageLog, deleteDamageLog } from '@/services/api';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Plus, Trash2, Edit, RefreshCw, Info, ShieldAlert,
  AlertTriangle, CheckCircle2, Clock, Filter, X, Download, Search
} from 'lucide-react';

const SEVERITY_CONFIG = {
  low: { label: 'Low', color: 'bg-blue-100 text-blue-800', dot: 'bg-blue-500' },
  medium: { label: 'Medium', color: 'bg-amber-100 text-amber-800', dot: 'bg-amber-500' },
  high: { label: 'High', color: 'bg-orange-100 text-orange-800', dot: 'bg-orange-500' },
  critical: { label: 'Critical', color: 'bg-rose-100 text-rose-800', dot: 'bg-rose-500' },
};

const STATUS_CONFIG = {
  open: { label: 'Open', color: 'bg-red-100 text-red-800', icon: AlertTriangle },
  in_progress: { label: 'In Progress', color: 'bg-amber-100 text-amber-800', icon: Clock },
  resolved: { label: 'Resolved', color: 'bg-green-100 text-green-800', icon: CheckCircle2 },
};

export const DamageLogPage = () => {
  const { isAdmin, isGuest } = useAuth();
  const [logs, setLogs] = useState([]);
  const [config, setConfig] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [resourceFilter, setResourceFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [personSearch, setPersonSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [editingLog, setEditingLog] = useState(null);
  const [formData, setFormData] = useState({
    date: new Date().toISOString().slice(0, 10),
    person: '',
    resource: '',
    damage_type: '',
    severity: 'medium',
    notes: '',
    status: 'open',
  });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (resourceFilter) params.resource = resourceFilter;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (personSearch.trim()) params.person = personSearch.trim();

      const [logsData, configData, analyticsData] = await Promise.all([
        getDamageLogs(params),
        getDamageLogConfig(),
        getDamageLogAnalytics(),
      ]);
      setLogs(logsData);
      setConfig(configData);
      setAnalytics(analyticsData);
    } catch (error) {
      toast.error('Failed to load damage logs');
    } finally {
      setLoading(false);
    }
  };

  // Name search is debounced so typing doesn't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => { loadData(); }, 350);
    return () => clearTimeout(t);
  }, [statusFilter, resourceFilter, personSearch]);

  const applyDateFilter = () => loadData();
  const clearFilters = () => {
    setStatusFilter('all');
    setResourceFilter('');
    setDateFrom('');
    setDateTo('');
    setPersonSearch('');
  };

  const resetForm = () => {
    setFormData({
      date: new Date().toISOString().slice(0, 10),
      person: '',
      resource: '',
      damage_type: '',
      severity: 'medium',
      notes: '',
      status: 'open',
    });
  };

  const handleCreate = async () => {
    if (!formData.person || !formData.resource || !formData.damage_type) {
      toast.error('Please fill in Person, Resource, and Type of Damage');
      return;
    }
    try {
      await createDamageLog(formData);
      toast.success('Damage log created');
      setCreateOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to create damage log');
    }
  };

  const handleUpdate = async () => {
    if (!editingLog) return;
    try {
      await updateDamageLog(editingLog.log_id, formData);
      toast.success('Damage log updated');
      setEditingLog(null);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to update');
    }
  };

  const handleDelete = async (logId) => {
    if (!window.confirm('Delete this damage log entry?')) return;
    try {
      await deleteDamageLog(logId);
      toast.success('Damage log deleted');
      loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to delete');
    }
  };

  const openEdit = (log) => {
    setFormData({
      date: log.date,
      person: log.person,
      resource: log.resource,
      damage_type: log.damage_type,
      severity: log.severity,
      notes: log.notes,
      status: log.status,
    });
    setEditingLog(log);
  };

  const activeFilters = [statusFilter !== 'all', resourceFilter, dateFrom, dateTo, personSearch].filter(Boolean).length;

  const handleExport = async (format) => {
    try {
      const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const resp = await fetch(`${API_URL}/api/export/damage-logs?format=${format}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error('Export failed');
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `damage_logs.${format === 'excel' ? 'xlsx' : 'pdf'}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported as ${format === 'excel' ? 'Excel' : 'PDF'}`);
    } catch (error) {
      toast.error('Failed to export damage logs');
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="damage-log-page">
        {isGuest && (
          <div className="flex items-center gap-3 px-4 py-3 bg-sky-50 border border-sky-200 rounded-lg" data-testid="demo-data-banner">
            <Info className="h-5 w-5 text-sky-600 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-sky-800">Sample Data Preview</p>
              <p className="text-xs text-sky-600">The entries below are illustrative examples.</p>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Damage Log</h1>
            <p className="text-muted-foreground font-body mt-1">Track and manage equipment damage reports</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => handleExport('excel')} data-testid="export-excel-btn">
              <Download className="h-4 w-4 mr-2" /> Excel
            </Button>
            <Button variant="outline" size="sm" onClick={() => handleExport('pdf')} data-testid="export-pdf-btn">
              <Download className="h-4 w-4 mr-2" /> PDF
            </Button>
            <Button variant="outline" onClick={loadData} data-testid="refresh-btn">
              <RefreshCw className="h-4 w-4 mr-2" /> Refresh
            </Button>
            {!isGuest && (
              <Button onClick={() => { resetForm(); setCreateOpen(true); }} data-testid="new-damage-log-btn">
                <Plus className="h-4 w-4 mr-2" /> Report Damage
              </Button>
            )}
          </div>
        </div>

        {/* Status Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card
            className={`cursor-pointer transition-all hover:shadow-md ${statusFilter === 'all' ? 'ring-2 ring-blue-500 shadow-md' : ''}`}
            onClick={() => setStatusFilter('all')}
            data-testid="stat-total"
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                  <ShieldAlert className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{analytics?.total || 0}</p>
                  <p className="text-sm text-muted-foreground">Total</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card
            className={`cursor-pointer transition-all hover:shadow-md ${statusFilter === 'open' ? 'ring-2 ring-red-500 shadow-md' : ''}`}
            onClick={() => setStatusFilter('open')}
            data-testid="stat-open"
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center">
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-red-600">{analytics?.open || 0}</p>
                  <p className="text-sm text-muted-foreground">Open</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card
            className={`cursor-pointer transition-all hover:shadow-md ${statusFilter === 'in_progress' ? 'ring-2 ring-amber-500 shadow-md' : ''}`}
            onClick={() => setStatusFilter('in_progress')}
            data-testid="stat-in-progress"
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
                  <Clock className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-amber-600">{analytics?.in_progress || 0}</p>
                  <p className="text-sm text-muted-foreground">In Progress</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card
            className={`cursor-pointer transition-all hover:shadow-md ${statusFilter === 'resolved' ? 'ring-2 ring-green-500 shadow-md' : ''}`}
            onClick={() => setStatusFilter('resolved')}
            data-testid="stat-resolved"
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center">
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-600">{analytics?.resolved || 0}</p>
                  <p className="text-sm text-muted-foreground">Resolved</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="border-dashed">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-end gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Person</Label>
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                  <Input value={personSearch} onChange={(e) => setPersonSearch(e.target.value)}
                         placeholder="Search by name" className="w-52 h-9 pl-8"
                         data-testid="person-search" />
                  {personSearch && (
                    <button type="button" onClick={() => setPersonSearch('')}
                            data-testid="person-search-clear"
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Resource</Label>
                <Select value={resourceFilter || 'all'} onValueChange={(v) => setResourceFilter(v === 'all' ? '' : v)}>
                  <SelectTrigger className="w-40 h-9" data-testid="resource-filter">
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    {config?.resource_types?.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">From</Label>
                <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-40 h-9" data-testid="date-from" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">To</Label>
                <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-40 h-9" data-testid="date-to" />
              </div>
              <Button variant="outline" size="sm" className="h-9" onClick={applyDateFilter} data-testid="apply-filter">
                <Filter className="h-3.5 w-3.5 mr-1.5" /> Apply
              </Button>
              {activeFilters > 0 && (
                <Button variant="ghost" size="sm" className="h-9" onClick={clearFilters} data-testid="clear-filters">
                  <X className="h-3.5 w-3.5 mr-1.5" /> Clear
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Damage Log Table */}
        {loading ? (
          <div className="flex items-center justify-center h-40">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <ShieldAlert className="h-5 w-5 text-blue-500" />
                    {statusFilter === 'all' ? 'All Damage Reports' : `${STATUS_CONFIG[statusFilter]?.label || ''} Reports`}
                  </CardTitle>
                  <CardDescription>{logs.length} entr{logs.length !== 1 ? 'ies' : 'y'}</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {logs.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="damage-log-table">
                    <thead>
                      <tr className="border-b border-muted text-left">
                        <th className="pb-3 pr-4 font-medium text-muted-foreground">Date</th>
                        <th className="pb-3 pr-4 font-medium text-muted-foreground">Person</th>
                        <th className="pb-3 pr-4 font-medium text-muted-foreground">Resource</th>
                        <th className="pb-3 pr-4 font-medium text-muted-foreground">Type of Damage</th>
                        <th className="pb-3 pr-4 font-medium text-muted-foreground">Severity</th>
                        <th className="pb-3 pr-4 font-medium text-muted-foreground">Status</th>
                        <th className="pb-3 pr-4 font-medium text-muted-foreground">Notes</th>
                        {isAdmin && <th className="pb-3 font-medium text-muted-foreground text-center">Actions</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {logs.map(log => {
                        const sev = SEVERITY_CONFIG[log.severity] || SEVERITY_CONFIG.medium;
                        const st = STATUS_CONFIG[log.status] || STATUS_CONFIG.open;
                        const StIcon = st.icon;
                        return (
                          <tr key={log.log_id} className="border-b border-muted/50 hover:bg-muted/20" data-testid={`log-row-${log.log_id}`}>
                            <td className="py-3 pr-4 whitespace-nowrap font-mono text-xs">{log.date}</td>
                            <td className="py-3 pr-4 font-medium">{log.person}</td>
                            <td className="py-3 pr-4">
                              <Badge variant="outline" className="text-xs">{log.resource}</Badge>
                            </td>
                            <td className="py-3 pr-4">{log.damage_type}</td>
                            <td className="py-3 pr-4">
                              <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold ${sev.color}`}>
                                <span className={`w-1.5 h-1.5 rounded-full ${sev.dot}`} />
                                {sev.label}
                              </span>
                            </td>
                            <td className="py-3 pr-4">
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${st.color}`}>
                                <StIcon className="h-3 w-3" />
                                {st.label}
                              </span>
                            </td>
                            <td className="py-3 pr-4 max-w-[200px] truncate text-muted-foreground text-xs" title={log.notes}>
                              {log.notes || '-'}
                            </td>
                            {isAdmin && (
                              <td className="py-3 text-center">
                                <div className="flex items-center justify-center gap-1">
                                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(log)} data-testid={`edit-log-${log.log_id}`}>
                                    <Edit className="h-3.5 w-3.5" />
                                  </Button>
                                  <Button variant="ghost" size="icon" className="h-7 w-7 text-rose-500 hover:text-rose-700" onClick={() => handleDelete(log.log_id)} data-testid={`delete-log-${log.log_id}`}>
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </Button>
                                </div>
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12">
                  <ShieldAlert className="h-10 w-10 mx-auto mb-3 text-muted-foreground opacity-50" />
                  <p className="text-muted-foreground">No damage reports found</p>
                  {!isGuest && statusFilter === 'all' && (
                    <Button variant="outline" className="mt-3" onClick={() => { resetForm(); setCreateOpen(true); }}>
                      <Plus className="h-4 w-4 mr-2" /> Report first damage
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Create / Edit Dialog */}
        <Dialog open={createOpen || !!editingLog} onOpenChange={(open) => { if (!open) { setCreateOpen(false); setEditingLog(null); resetForm(); } }}>
          <DialogContent className="sm:max-w-[520px]">
            <DialogHeader>
              <DialogTitle data-testid="damage-dialog-title">{editingLog ? 'Edit Damage Report' : 'Report Damage'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Date *</Label>
                  <Input type="date" value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} data-testid="form-date" />
                </div>
                <div className="space-y-2">
                  <Label>Person *</Label>
                  <Input value={formData.person} onChange={(e) => setFormData({ ...formData, person: e.target.value })} placeholder="Name of person" data-testid="form-person" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Resource *</Label>
                  <Select value={formData.resource || 'none'} onValueChange={(v) => setFormData({ ...formData, resource: v === 'none' ? '' : v })}>
                    <SelectTrigger data-testid="form-resource"><SelectValue placeholder="Select resource" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Select...</SelectItem>
                      {config?.resource_types?.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Type of Damage *</Label>
                  <Select value={formData.damage_type || 'none'} onValueChange={(v) => setFormData({ ...formData, damage_type: v === 'none' ? '' : v })}>
                    <SelectTrigger data-testid="form-damage-type"><SelectValue placeholder="Select type" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Select...</SelectItem>
                      {config?.damage_types?.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Severity</Label>
                  <Select value={formData.severity} onValueChange={(v) => setFormData({ ...formData, severity: v })}>
                    <SelectTrigger data-testid="form-severity"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(SEVERITY_CONFIG).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {editingLog && (
                  <div className="space-y-2">
                    <Label>Status</Label>
                    <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                      <SelectTrigger data-testid="form-status"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(STATUS_CONFIG).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} placeholder="Describe the damage..." rows={3} data-testid="form-notes" />
              </div>
              <Button
                onClick={editingLog ? handleUpdate : handleCreate}
                className="w-full"
                disabled={!formData.person || !formData.resource || !formData.damage_type}
                data-testid="submit-damage-log"
              >
                {editingLog ? 'Update Report' : 'Submit Report'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default DamageLogPage;
